// Revenue triage — Category rules viewer. Lists the sticky
// (property, merchant) → category memories the Mercury importer applies on
// future imports (created implicitly when a host categorizes a Mercury
// expense), with per-rule delete. Opened from the Expenses overflow menu.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { getCategoryLabel } from "./_shared";
import { Trash2 } from "lucide-react";

interface CategoryRule {
  id: number;
  merchantNormalized: string;
  category: string;
  propertyId: number;
  propertyName: string | null;
  updatedAt: string;
}

export function CategoryRulesDialog({
  open,
  onOpenChange,
  customCategories,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customCategories?: { label: string; value: string; isBalanceSheet?: boolean }[];
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const rulesQuery = useQuery<{ rules: CategoryRule[] }>({
    queryKey: ["/api/app/category-rules"],
    enabled: open,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/category-rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/category-rules"] });
      toast({ title: "Rule deleted", description: "Future imports will no longer auto-apply it." });
    },
    onError: (err) => {
      toast({
        title: "Could not delete rule",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const rules = rulesQuery.data?.rules ?? [];

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!deleteMutation.isPending) onOpenChange(next); }}>
      <DialogContent
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => { if (deleteMutation.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (deleteMutation.isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Category rules</DialogTitle>
          <DialogDescription>
            When you categorize a Mercury expense, PadPulse remembers the choice per property and
            merchant and applies it to future imports. Delete a rule to stop auto-applying it.
          </DialogDescription>
        </DialogHeader>

        {rulesQuery.isLoading ? (
          <div className="py-8 text-center text-sm text-gray-400" data-testid="loading-category-rules">
            Loading rules…
          </div>
        ) : rules.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500" data-testid="empty-category-rules">
            No rules yet. Categorize a Mercury expense on a property and it will show up here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-category-rules">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-400">
                  <th className="py-2 pr-3 font-medium">Merchant</th>
                  <th className="py-2 pr-3 font-medium">Property</th>
                  <th className="py-2 pr-3 font-medium">Category</th>
                  <th className="py-2 w-10" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id} className="border-b last:border-0" data-testid={`row-category-rule-${rule.id}`}>
                    <td className="py-2 pr-3 font-medium text-gray-900">{rule.merchantNormalized}</td>
                    <td className="py-2 pr-3 text-gray-600">{rule.propertyName ?? "—"}</td>
                    <td className="py-2 pr-3 text-gray-600">{getCategoryLabel(rule.category, customCategories)}</td>
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-red-600"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(rule.id)}
                        aria-label={`Delete rule for ${rule.merchantNormalized}`}
                        data-testid={`button-delete-rule-${rule.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
